import { Dialog, DialogContent, DialogHeader, Stack, Typography } from '@airbus/components-react';
import { Button, CardActions } from '@airbus/components-react';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import styles from './DisplayResults.module.css';
import { configureFileName, getTimeObj } from './exportFunctions';
import { useAlertBar } from '../../context/AlertBarContext';
import { nanoid } from 'nanoid';

type UploadResult = {
  [key: string]: any;
  htz: string;
  message: string;
  rowNo: number;
};

type DisplayResultsProps = {
  resultData: {
    data: Array<UploadResult>;
    errors: Array<UploadResult>;
    success?: boolean;
  };
  primaryKey: string;
  setResultData: (params) => void;
  openResults: boolean;
  setOpenResults: (params) => void;
  fileName: string;
};

export function DisplayResults(props: DisplayResultsProps) {
  const { resultData, openResults, setOpenResults, fileName } = props;
  const { data = [], errors = [] } = resultData;

  const time = getTimeObj();
  const formattedFileName = configureFileName(
    { ...time },
    fileName ? `${fileName}_{YYYY}{MM}{DD}_{hh}:{mm}:{ss}` : `StatusReport`,
  );

  const { t } = useLanguageContextAPI();
  const { setAlertbar } = useAlertBar();

  const handleDownload = () => {
    if (formattedItems?.length) {
      const fileContent = formattedItems.join('\n');
      const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formattedFileName}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setAlertbar([
        {
          id: nanoid(),
          children: t('fileUpload.download_success'),
          severity: 'success',
          autoHide: true,
        },
      ]);
    }
  };

  const allItems = [
    ...data.map((obj) => ({ ...obj, isError: false })),
    ...errors.map((obj) => ({ ...obj, isError: true })),
  ];

  allItems.sort((a, b) => {
    const aRow = typeof a.rowNo === 'number' ? a.rowNo : Infinity;
    const bRow = typeof b.rowNo === 'number' ? b.rowNo : Infinity;
    return aRow - bRow;
  });

  const formattedItems = allItems.map((item) => {
    return (item?.rowNo >= 0 ? `Line ${item.rowNo + 2}` : '') + `: ${item.htz} ${item.message}`;
  });

  return (
    <Dialog
      open={openResults}
      onClose={() => {
        setOpenResults(false);
      }}
    >
      <DialogHeader>{t('fileUpload.upload_results')}</DialogHeader>
      <DialogContent>
        <div className={styles.innerViewBox}>
          <div className={styles.box}>
            <Stack spacing='1-x'>
              {allItems?.map((item) => (
                <div
                  key={item.rowNo}
                  style={{
                    backgroundColor: item.isError ? '#fff3f3' : 'rgb(244, 247, 255)',
                    color: item.isError ? 'error.main' : 'text.primary',
                  }}
                >
                  <Typography variant='small' color='primary' className={styles.innerItemBox}>
                    {(item?.rowNo >= 0 ? `Line ${item.rowNo + 2}` : '') +
                      `: ${item.htz} ${item.message}`}
                  </Typography>
                </div>
              ))}
            </Stack>
          </div>
        </div>
      </DialogContent>
      <CardActions className={styles.justifyContent}>
        <Button onClick={handleDownload}>{t('fileUpload.download_report')}</Button>
      </CardActions>
    </Dialog>
  );
}
